import React from 'react'
import moment from 'moment'
import _ from 'lodash'

import InvoiceActions from '../../actions/InvoiceActions.js'

import {Alert, Button, Card, Colors, Link, Modal, P, Spinner, Row, S1, Popup} from '../UI/index.js'
import {formSpacing, Check, TextArea} from '../UI/forms/index.js'
import {Customer, Invoice, InvoiceAttachment, Reseller} from '../../../types/types.js'
import {formatPrice, parsePrice} from '../../../utils/price.js'
import Languages from '@utils/Languages.js'
import {getColliTypeName} from '../../../utils/getColliTypeName.js'
import {getColliTypesSummary, getIncludedColli} from '../../../utils/colliTypesIncluded.js'
import {showColliBarcodeSpecification} from '../../../utils/showColliBarcodeSpecification.js'
import {emit} from 'src/socketio/socketEmit.js'
import {isMinimumRevenueLine} from '../../../utils/minimumRevenue.js'
import t from '../../helpers/Languages.js'
import type {PopupRef} from '../UI/overlays/Popup.js'

interface InvoiceModalProps {
    history: any
    customers: {
        [customerId: string]: Customer
    }
    reseller: Reseller
    toPDF: (invoice: Invoice, callback: (err: string, response: InvoiceAttachment) => void) => void
    onClickSendInvoice: (invoice: Invoice, callback: (err: string) => void) => void
}

interface InvoiceModalState {
    modalIsOpen: boolean
    invoice: Invoice
    selected: number[]
    notes: string
    loading: boolean
    error: string
    invoiceLoading?: boolean
    invoiceError?: string
}

class InvoiceModal extends React.Component<InvoiceModalProps, InvoiceModalState> {
    initialState: InvoiceModalState
    popup: PopupRef

    constructor(props) {
        super(props)

        this.initialState = {
            modalIsOpen: false,
            invoice: {price: '0', invoiceLines: []},
            selected: [],
            notes: '',
            loading: false,
            error: ''
        }

        this.state = structuredClone(this.initialState)
    }

    open(id) {
        if (typeof id === 'object') {
            const invoice = this.sortByCustomer(id)
            const selected = [...Array(invoice.invoiceLines.length).keys()]
            console.log(invoice)
            this.setState({modalIsOpen: true, invoice, selected, notes: invoice.notes || ''})
        } else {
            this.setState({modalIsOpen: true, invoiceLoading: true})

            InvoiceActions.getOne(id, (err, invoice) => {
                if (err) {
                    this.setState({invoiceLoading: false, invoiceError: err})
                } else {
                    console.log(invoice)
                    invoice = this.sortByCustomer(invoice)
                    const selected = [...Array(invoice.invoiceLines.length).keys()]
                    this.setState({invoiceLoading: false, invoiceError: null, invoice, selected, notes: invoice.notes || ''})
                }
            })
        }
    }

    // The invoice of a main customer can have lines of the customers it invoices for: these are sorted per customer (the
    // main customer first, then the other customers by name) and then on date
    sortByCustomer(invoice: Invoice): Invoice {
        const {customers} = this.props

        if (!this.hasMultipleCustomers(invoice)) {
            return invoice
        }

        const invoiceLines = _.sortBy(invoice.invoiceLines, [
            (invoiceLine) => invoiceLine.customer === invoice.customer ? 0 : 1,
            (invoiceLine) => customers[invoiceLine.customer]?.name || '',
            'customer',
            'date'
        ])

        return {...invoice, invoiceLines}
    }

    hasMultipleCustomers(invoice: Invoice) {
        return invoice.invoiceLines.some((invoiceLine) => invoiceLine.customer && invoiceLine.customer !== invoice.customer)
    }

    close() {
        const {invoice, notes} = this.state

        if (invoice.invoiced && notes !== invoice.notes) {
            InvoiceActions.editNotes(invoice._id, notes, (err) => {
                if (err) {
                    this.setState({error: err})
                } else {
                    this.setState(this.initialState)
                }
            })
        } else {
            this.setState(this.initialState)
        }
    }

    async onClickSendInvoice() {
        let {invoice, selected} = this.state
        const {reseller} = this.props

        if (selected.length > 0) {
            const invoiceLines = invoice.invoiceLines.filter((invoiceLine, index) => {
                return selected.indexOf(index) > -1
            })

            invoice.invoiceLines = invoiceLines

            if (!await this.popup.open('Facturen aanmaken', `Facturen worden aangemaakt${reseller.settings.administration.api ? ' en verstuurd naar het gekoppelde boekhoudsysteem' : ''}, hierna kunnen ze worden gedownload.`)) return

            try {
                ;({data: invoice} = await emit<Invoice>('invoices.send', invoice))

                this.popup.close()
                this.setState({invoice})

                InvoiceActions.get(true)
            } catch (err) {
                this.popup.setError(err instanceof Error ? err.message : err.toString())
            }
        }
    }

    async onClickOpenAttachment() {
        const {invoice} = this.state

        this.setState({loading: true, error: ''})

        try {
            const response = await emit<{pdf: string}>('invoices.toPDF', invoice)

            this.setState({loading: false})

            const raw = window.atob(response.data.pdf)
            const rawLength = raw.length
            const array = new Uint8Array(new ArrayBuffer(rawLength))

            for (let i = 0; i < rawLength; i++) {
                array[i] = raw.charCodeAt(i)
            }
            const blob = new Blob([array], {type: 'application/pdf'})
            const blobUrl = URL.createObjectURL(blob)
            window.open(blobUrl)
        } catch (err) {
            this.setState({loading: false, error: err instanceof Error ? err.message : err.toString()})
        }
    }

    async onClickRemoveInvoice() {
        const {invoice} = this.state
        const {reseller} = this.props

        if (!await this.popup.open('Factuur verwijderen?', `Verwijderde facturen worden weer zichtbaar als concept en kunnen opnieuw worden aangemaakt. ${reseller.settings.administration.api ? `Facturen worden niet verwijderd in ${reseller.settings.administration.api}` : ''}`)) return

        try {
            await emit('invoices.remove', invoice._id)

            this.popup.close()
            this.close()
        } catch (err) {
            this.popup.setError(err instanceof Error ? err.message : err.toString())
            return
        }
    }

    onSelectInvoiceLine(i) {
        const {selected} = this.state
        const index = selected.indexOf(i)

        if (index > -1) {
            selected.splice(index, 1)
        } else {
            selected.push(i)
        }

        selected.sort((a, b) => (a - b)) // Sort numerically
        this.setState({selected})
    }

    onSelectAll() {
        let {invoice, selected} = this.state

        if (invoice.invoiceLines.length !== selected.length) {
            selected = [...Array(invoice.invoiceLines.length).keys()]
        } else {
            selected = []
        }

        this.setState({selected})
    }

    onChangeNotes(event) {
        this.setState({notes: event.target.value})
    }

    render() {
        const {modalIsOpen, invoiceLoading, invoiceError, invoice, selected, notes, loading, error} = this.state
        const {reseller, history, customers} = this.props

        const status = invoice.invoiced ? 'Gefactureerd' : 'Concept'
        const statusColor = invoice.invoiced ? Colors.completed : Colors.expected

        let showPriceExplanationOrders = false

        if (reseller.settings.administration.showPriceExplanationOrders) {
            showPriceExplanationOrders = true
        }

        const customer = customers[invoice.customer]
        if (customer) {
            if (customer.settings.orders.showPriceExplanation === 'yes') {
                showPriceExplanationOrders = true
            }

            if (customer.settings.orders.showPriceExplanation === '') {
                showPriceExplanationOrders = false
            }
        }

        const showColliBarcodes = showColliBarcodeSpecification(reseller, customer)
        const showCustomer = this.hasMultipleCustomers(invoice)

        let showPriceExplanationParcels = false

        if (reseller.settings.administration.showPriceExplanationParcels) {
            showPriceExplanationParcels = true
        }

        if (customer) {
            if (customer.settings.parcels.showPriceExplanation === 'yes') {
                showPriceExplanationParcels = true
            }

            if (customer.settings.parcels.showPriceExplanation === '') {
                showPriceExplanationParcels = false
            }
        }

        return (
            <Modal
                show={modalIsOpen}
                style={{width: 1200, maxWidth: '90%'}}
                title='Factuur specificatie'
                closeButton
                onClose={this.close.bind(this)}
            >

                {invoiceLoading ?
                    <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250}}>
                        <Spinner color={Colors.textDark} />
                    </div> :
                    <>
                        <Card shadow style={{marginBottom: 24, padding: 12}}>
                            <Row style={{marginBottom: 24}}>
                                <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, flexDirection: 'column', minWidth: 'auto'}}>
                                    {customer && <P icon='mdi mdi-store-outline'>{`${customer.name} ${customer.debtorCode ? ` - [${customer.debtorCode}]` : ''}`}</P>}
                                    <P icon='mdi mdi-calendar'>{`${moment(invoice.startDate).format('DD-MM-YYYY')} t/m ${moment(invoice.endDate).format('DD-MM-YYYY')}`}</P>
                                    <P icon='mdi mdi-currency-eur'>{invoice.price}</P>

                                </Row>

                                <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, flexDirection: 'column', minWidth: 'auto'}}>

                                    <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, justifyContent: 'center', alignItems: 'center', background: statusColor, minHeight: 42, marginBottom: 12, minWidth: 'auto'}}>
                                        <S1 ellipsis>{status}</S1>
                                    </Row>

                                    <div style={{display: 'flex', justifyContent: 'space-between', gap: formSpacing.section}}>
                                        {!invoice.invoiced &&
                                            <Button
                                                label='Factuur aanmaken'
                                                icon='mdi mdi-format-list-bulleted'
                                                onClick={this.onClickSendInvoice.bind(this)}
                                            />
                                        }

                                        {invoice.invoiced &&
                                            <>
                                                <Button
                                                    style={{flex: 1}}
                                                    variant='outline'
                                                    label='Downloaden'
                                                    icon='mdi mdi-download'
                                                    loading={loading}
                                                    onClick={this.onClickOpenAttachment.bind(this)}
                                                />

                                                <Button
                                                    style={{flex: 1}}
                                                    variant='outline'
                                                    label={Languages.delete['NL']}
                                                    icon='mdi mdi-delete'
                                                    onClick={this.onClickRemoveInvoice.bind(this)}
                                                />
                                            </>

                                        }
                                    </div>
                                </Row>
                            </Row>

                            <Row>
                                <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, minWidth: 'auto'}}>
                                    {invoice.invoiced &&
                                        <TextArea
                                            label='Notities'
                                            value={notes}
                                            onChange={this.onChangeNotes.bind(this)}
                                        />
                                    }
                                </Row>
                            </Row>
                        </Card>

                        <Card shadow style={{maxHeight: 430, marginBottom: 24, overflowY: 'auto'}}>
                            <div style={{flex: 1}}>
                                <table style={{width: '100%', textAlign: 'left'}}>
                                    <thead>
                                        <tr style={{padding: 3}}>
                                            {!invoice.invoiced &&
                                                <th>
                                                    <Check
                                                        checked={selected.length === invoice.invoiceLines.length}
                                                        onChange={this.onSelectAll.bind(this)}
                                                    />
                                                </th>
                                            }
                                            <th style={{padding: 3, borderBottom: `1px solid ${Colors.grey30}`}}><S1>Datum</S1></th>
                                            {showCustomer &&
                                                <th style={{padding: 3, borderBottom: `1px solid ${Colors.grey30}`}}><S1>{t('customer')}</S1></th>
                                            }
                                            <th style={{padding: 3, borderBottom: `1px solid ${Colors.grey30}`}}><S1>Omschrijving</S1></th>
                                            <th style={{padding: 3, borderBottom: `1px solid ${Colors.grey30}`}}><S1>Prijs</S1></th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {invoice.invoiceLines.map((invoiceLine, index) => {
                                            return (
                                                <tr key={`invoiceLine${index}`}>
                                                    {!invoice.invoiced &&
                                                        <td style={{verticalAlign: 'top'}}>
                                                            <Check
                                                                checked={selected.indexOf(index) > -1}
                                                                onChange={this.onSelectInvoiceLine.bind(this, index)}
                                                                style={{marginTop: -6}}
                                                            />
                                                        </td>
                                                    }
                                                    <td style={{width: 100, verticalAlign: 'top'}}><P>{moment(invoiceLine.date).format('DD-MM-YYYY')}</P></td>
                                                    {showCustomer &&
                                                        <td style={{width: 200, padding: 3, verticalAlign: 'top'}}><P>{customers[invoiceLine.customer]?.name || ''}</P></td>
                                                    }
                                                    <td style={{padding: 3, verticalAlign: 'top'}}>
                                                        {invoiceLine.type === 'order' &&
                                                            <>
                                                                <Link onClick={() => history.push('/planning', {orderId: invoiceLine._id})}>{invoiceLine.description}</Link>
                                                                {showPriceExplanationOrders && invoiceLine.priceExplanation && !isMinimumRevenueLine(invoiceLine) &&
                                                                    <div style={{marginLeft: 20}}>
                                                                        {invoiceLine.correctionInvoicedAmount &&
                                                                            <Row>
                                                                                <P style={{flex: 1}}>Gefactureerd bedrag</P>
                                                                                <P style={{marginRight: 32}}>€ {invoiceLine.correctionInvoicedAmount}</P>
                                                                            </Row>
                                                                        }
                                                                        {invoiceLine.priceExplanation.overruledPrice && invoiceLine.priceExplanation.overruledPrice !== '0,00' &&
                                                                            <Row>
                                                                                <P style={{flex: 1}}>Prijs</P>
                                                                                <P style={{marginRight: 32}}>€ {invoiceLine.priceExplanation.overruledPrice}</P>
                                                                            </Row>
                                                                        }
                                                                        {invoiceLine.priceExplanation.minimumRate ?
                                                                            <Row>
                                                                                <P style={{flex: 1}}>Starttarief</P>
                                                                                <P style={{marginRight: 32}}>€ {invoiceLine.priceExplanation.minimumRate}</P>
                                                                            </Row> :
                                                                            <>
                                                                                {invoiceLine.priceExplanation.startRate &&
                                                                                    <Row>
                                                                                        <P style={{flex: 1}}>Starttarief</P>
                                                                                        <P style={{marginRight: 32}}>€ {invoiceLine.priceExplanation.startRate}</P>
                                                                                    </Row>
                                                                                }

                                                                                {invoiceLine.priceExplanation.feeExtraStop && invoiceLine.priceExplanation.feeExtraStop.subtotal !== '0,00' &&
                                                                                    <Row>
                                                                                        <Row>
                                                                                            <P style={{flex: 1}}>{invoiceLine.priceExplanation.feeExtraStop.nrOfStops} stop{invoiceLine.priceExplanation.feeExtraStop.nrOfStops > 1 ? 's' : ''}</P>
                                                                                            <P style={{marginRight: 32}}>€ {invoiceLine.priceExplanation.feeExtraStop.subtotal}</P>
                                                                                        </Row>
                                                                                    </Row>
                                                                                }

                                                                                {invoiceLine.priceExplanation.feeForPickupAddress && invoiceLine.priceExplanation.feeForPickupAddress.subtotal !== '0,00' &&
                                                                                    <Row>
                                                                                        <Row>
                                                                                            <P style={{flex: 1}}>{invoiceLine.priceExplanation.feeForPickupAddress.nrOfAddresses} ophaaladres{invoiceLine.priceExplanation.feeForPickupAddress.nrOfAddresses > 1 ? 'sen' : ''}</P>
                                                                                            <P style={{marginRight: 32}}>€ {invoiceLine.priceExplanation.feeForPickupAddress.subtotal}</P>
                                                                                        </Row>
                                                                                    </Row>
                                                                                }

                                                                                {invoiceLine.priceExplanation.feeForDeliveryAddress && invoiceLine.priceExplanation.feeForDeliveryAddress.subtotal !== '0,00' &&
                                                                                    <Row>
                                                                                        <Row>
                                                                                            <P style={{flex: 1}}>{invoiceLine.priceExplanation.feeForDeliveryAddress.nrOfAddresses} bezorgadres{invoiceLine.priceExplanation.feeForDeliveryAddress.nrOfAddresses > 1 ? 'sen' : ''}</P>
                                                                                            <P style={{marginRight: 32}}>€ {invoiceLine.priceExplanation.feeForDeliveryAddress.subtotal}</P>
                                                                                        </Row>
                                                                                    </Row>
                                                                                }

                                                                                {invoiceLine.priceExplanation.feeForExtraCollo && invoiceLine.priceExplanation?.feeForExtraCollo.subtotal !== '0,00' &&
                                                                                    <Row>
                                                                                        <Row>
                                                                                            <P style={{flex: 1}}>{invoiceLine.priceExplanation.feeForExtraCollo.nrOfColli} colli</P>
                                                                                            <P style={{marginRight: 32}}>€ {invoiceLine.priceExplanation.feeForExtraCollo.subtotal}</P>
                                                                                        </Row>
                                                                                    </Row>
                                                                                }
                                                                                {/* The per barcode list already specifies every collo and its price, so the colli totaal would repeat it */}
                                                                                {invoiceLine.priceExplanation.colliTypes?.length > 0 && !(showColliBarcodes && invoiceLine.colli?.length > 0) &&
                                                                                    <Row>
                                                                                        <P style={{flex: 1}}>{t('priceExplanationColliTotal', getColliTypesSummary(invoiceLine.priceExplanation.colliTypes))}</P>
                                                                                        <P style={{marginRight: 32}}>€ {invoiceLine.priceExplanation.colliTypes.reduce((sum, colliType) => sum + parseFloat(colliType.subtotal.replace(',', '.')), 0).toFixed(2).replace('.', ',')}</P>
                                                                                    </Row>
                                                                                }
                                                                                {showColliBarcodes && invoiceLine.colli?.length > 0 &&
                                                                                    invoiceLine.colli.map((collo, index, colli) => {
                                                                                        const colliTypeName = getColliTypeName(collo.type, reseller)
                                                                                        const included = getIncludedColli(colli.map((collo) => getColliTypeName(collo.type, reseller)), invoiceLine.priceExplanation.colliTypes)[index]
                                                                                        const colliTypeFee = invoiceLine.priceExplanation.colliTypes?.find((type) => type.type === colliTypeName)

                                                                                        return (
                                                                                            <Row key={`colli-barcode-${index}`}>
                                                                                                <P style={{flex: 1, marginLeft: 20}}>{collo.barcode} - {colliTypeName}</P>
                                                                                                {(included || colliTypeFee?.fee) &&
                                                                                                    <P style={{marginRight: 32 + 20}}>{included ? t('priceExplanationColloIncluded') : `€ ${colliTypeFee.fee}`}</P>
                                                                                                }
                                                                                            </Row>
                                                                                        )
                                                                                    })
                                                                                }
                                                                                {invoiceLine.priceExplanation.pickupAndDeliveryOptions?.length > 0 ?
                                                                                    <Row>
                                                                                        <Row>
                                                                                            <P style={{flex: 1}}>{invoiceLine.priceExplanation.pickupAndDeliveryOptions.length} Ophaal- / bezorgoptie{invoiceLine.priceExplanation.pickupAndDeliveryOptions.length > 1 ? 's' : ''}</P>
                                                                                            <P style={{marginRight: 32}}>€ {formatPrice(invoiceLine.priceExplanation.pickupAndDeliveryOptions.reduce((acc, option) => acc + parsePrice(option.subtotal), 0))}</P>
                                                                                        </Row>
                                                                                    </Row> :
                                                                                    null
                                                                                }
                                                                                {invoiceLine.priceExplanation.distanceFee &&
                                                                                    <Row>
                                                                                        <Row>
                                                                                            <P style={{flex: 1}}>Kilometerprijs {(invoiceLine.priceExplanation.distanceFee.distance - (invoiceLine.priceExplanation.distanceFee.distanceIncluded || 0)).toFixed(2).replace('.', ',')}km à €{invoiceLine.priceExplanation.distanceFee.fee}</P>
                                                                                            <P style={{marginRight: 32}}>€ {invoiceLine.priceExplanation.distanceFee.subtotal}</P>
                                                                                        </Row>
                                                                                    </Row>
                                                                                }

                                                                                {invoiceLine.priceExplanation.zoneFees &&
                                                                                    <Row>
                                                                                        <Row>
                                                                                            <P style={{flex: 1}}>Bezorggebied {invoiceLine.priceExplanation.zoneFees.zones[0]}{invoiceLine.priceExplanation.zoneFees.zones?.[1] ? ` - ${invoiceLine.priceExplanation.zoneFees.zones[1]}` : ''}</P>
                                                                                            <P style={{marginRight: 32}}>€ {invoiceLine.priceExplanation.zoneFees.subtotal}</P>
                                                                                        </Row>
                                                                                    </Row>
                                                                                }

                                                                                {invoiceLine.priceExplanation.fuelFee?.subtotal &&
                                                                                    <Row>
                                                                                        <Row>
                                                                                            <P style={{flex: 1}}>Brandstoftoeslag {invoiceLine.priceExplanation.fuelFee.percentage}%</P>
                                                                                            <P style={{marginRight: 32}}>€ {invoiceLine.priceExplanation.fuelFee.subtotal}</P>
                                                                                        </Row>
                                                                                    </Row>
                                                                                }

                                                                                {invoiceLine.priceExplanation.fees?.length > 0 ?
                                                                                    invoiceLine.priceExplanation?.fees.map((fee) => (
                                                                                        <Row key={fee.name}>
                                                                                            <P style={{flex: 1}}>{fee.name}</P>
                                                                                            <P style={{marginRight: 32}}>€ {fee.fee || fee.price}</P>
                                                                                        </Row>
                                                                                    )) :
                                                                                    null
                                                                                }

                                                                                {invoiceLine.priceExplanation.rounding &&
                                                                                    <Row>
                                                                                        <P style={{flex: 1}}>Afronding {[
                                                                                            {value: 1, title: 'Hele euro\'s'},
                                                                                            {value: 2.5, title: 'Veelvoud van €2,50'},
                                                                                            {value: 5, title: 'Veelvoud van €5,00'},
                                                                                            {value: 10, title: 'Veelvoud van €10,00'},
                                                                                            {value: 5.05, title: 'Veelvoud van €5,00 - €0,05'},
                                                                                            {value: 10.05, title: 'Veelvoud van €10,00 - €0,05'}
                                                                                        ].find((r) => r.value === invoiceLine.priceExplanation.rounding.rounding)?.title}: €{invoiceLine.priceExplanation.rounding.before}
                                                                                        </P>
                                                                                        <P style={{marginRight: 32}}>€ {invoiceLine.priceExplanation.rounding.subtotal}</P>
                                                                                    </Row>
                                                                                }
                                                                            </>
                                                                        }

                                                                    </div>
                                                                }
                                                            </>
                                                        }
                                                        {invoiceLine.type === 'parcel' &&
                                                            <>
                                                                <Link onClick={() => history.push('/zendingen', {parcelId: invoiceLine._id})}>{invoiceLine.description}</Link>
                                                                {invoiceLine.priceExplanation &&
                                                                    <div style={{marginLeft: 20}}>
                                                                        {invoiceLine.correctionInvoicedAmount &&
                                                                            <Row>
                                                                                <P style={{flex: 1}}>Gefactureerd bedrag</P>
                                                                                <P style={{marginRight: 32}}>€ {invoiceLine.correctionInvoicedAmount}</P>
                                                                            </Row>
                                                                        }
                                                                        {showPriceExplanationParcels && invoiceLine.priceExplanation.product?.price && invoiceLine.priceExplanation.product?.price !== invoiceLine.price &&
                                                                            <Row>
                                                                                <P style={{flex: 1}}>Zending tarief</P>
                                                                                <P style={{marginRight: 32}}>€ {typeof invoiceLine.priceExplanation.product?.price === 'string' ? invoiceLine.priceExplanation.product?.price : `${invoiceLine.priceExplanation.product?.price?.min} - € ${invoiceLine.priceExplanation.product?.price?.max}`}</P>
                                                                            </Row>
                                                                        }

                                                                        {invoiceLine.priceExplanation.options?.map((option, index) => {
                                                                            return (
                                                                                <Row key={`priceExplanation-fee-${index}`}>
                                                                                    <P style={{flex: 1}}>{option.name}</P>
                                                                                    {showPriceExplanationParcels &&
                                                                                        <P style={{marginRight: 32}}>€ {typeof option.price === 'string' ? option.price : `${option.price.min} - € ${option.price.max}`}</P>
                                                                                    }
                                                                                </Row>
                                                                            )
                                                                        })}

                                                                        {invoiceLine.priceExplanation.fees?.map((fee, index) => {
                                                                            return (
                                                                                <Row key={`priceExplanation-fee-${index}`}>
                                                                                    <P style={{flex: 1}}>{fee.name}</P>
                                                                                    {showPriceExplanationParcels &&
                                                                                        <P style={{marginRight: 32}}>€ {typeof fee.price === 'string' ? fee.price : `${fee.price.min} - € ${fee.price.max}`}</P>
                                                                                    }
                                                                                </Row>
                                                                            )
                                                                        })}
                                                                    </div>
                                                                }
                                                            </>
                                                        }
                                                        {invoiceLine.type === 'subscription' &&
                                                            <Link onClick={() => history.push('/abonnementen', {subscriptionId: invoiceLine._id || invoiceLine.subscriptionId})}>{invoiceLine.description}</Link>
                                                        }
                                                        {invoiceLine.type === 'shift' &&
                                                            <Link onClick={() => history.push('/diensten', {shiftId: invoiceLine._id})}>{invoiceLine.description}</Link>
                                                        }

                                                    </td>
                                                    <td style={{width: 100, verticalAlign: 'top'}}><P>{`€ ${invoiceLine.price}`}</P></td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </Card>

                        {error && <Alert style={{marginBottom: 6}} variant='danger'>{error}</Alert>}
                    </>
                }

                {invoiceError &&
                    <Alert style={{marginBottom: 6}} variant='danger'>{invoiceError}</Alert>
                }

                <Popup ref={(ref) => this.popup = ref} />
            </Modal>
        )
    }
}

export default InvoiceModal
